import React, { useEffect, useState } from "react";
import { Spin, Tooltip as TooltipAnT } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import "./SentimentWidget.css";
import { useGetSentimentsMutation } from "../../../store/api/lumi/sentiment";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { OverallSentimentDetailsModal } from "./OverallSentimentDetailModal";
import { useSelector } from "react-redux";

const COLORS = {
    Positive: "rgb(120, 208, 239)",
    Neutral: "#BDBCC8",
    Negative: "rgb(245, 150, 148)",
};

export const SentimentWidget = (props) => {
    const [chartData, setChartData] = useState([]);
    const [getSentiments] = useGetSentimentsMutation();
    const [isLoading, setIsLoading] = useState(false);

    const [selectedSentiment, setSelectedSentiment] = useState(null);
    const [topConceptsBySentiment, setTopConceptsBySentiment] = useState({});

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey]  || 16;
    });

    const getSentimentData = async () => {
        try {
            setIsLoading(true);
            const conceptFilter = getConceptFilter(props);

            const { data: dataConcepts } = await getSentiments({
                id: props.id,
                concept_selector: conceptFilter,
                filter: props.filters,
            });
            props.setFilterDocCount(dataConcepts.filter_count);

            const pieData = [
                { name: "Positive", value: dataConcepts?.sentiment_share.positive },
                { name: "Neutral", value: dataConcepts?.sentiment_share.neutral },
                { name: "Negative", value: dataConcepts?.sentiment_share.negative },
            ];

            const conceptsBySentiment = { Positive: [], Neutral: [], Negative: [] };
            dataConcepts.match_counts.forEach((concept) => {
                conceptsBySentiment.Positive.push({
                    name: concept.name,
                    count: ((concept.sentiment_share.positive * 100) || 0).toFixed(2) + "%",
                    concept:concept
                });
                conceptsBySentiment.Neutral.push({
                    name: concept.name,
                    count: ((concept.sentiment_share.neutral * 100) || 0).toFixed(2) + "%",
                    concept:concept
                });
                conceptsBySentiment.Negative.push({
                    name: concept.name,
                    count: ((concept.sentiment_share.negative * 100) || 0).toFixed(2) + "%",
                    concept:concept
                });
            });

            Object.keys(conceptsBySentiment).forEach((key) => {
                conceptsBySentiment[key].sort((a, b) => parseFloat(b.count) - parseFloat(a.count));
                conceptsBySentiment[key] = conceptsBySentiment[key].slice(0, 5);
            });

            setTopConceptsBySentiment(conceptsBySentiment);
            setChartData(pieData);
            setIsLoading(false);
        } catch (e) {
            console.log(e);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        const debouncedGetSentiment = debounce(getSentimentData, 1000);

        debouncedGetSentiment();

        return () => {
            debouncedGetSentiment.cancel();
        };
    }, [
        props.conceptList,
        props.seletctedType,
        props.selectedPageSize,
        props.filters,
        props.conceptTypeDay,
        props.selectedLocalConcepts.length,
    ]);

    const totalValue = chartData.reduce((acc, concept) => acc + concept.value, 0);

    const handleBarClick = (entry) => {
        setSelectedSentiment(entry.name);
    };

    if (isLoading) {
        return (
            <Spin
                className="spinner-by-center"
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }
    const total = chartData.reduce((sum, item) => sum + item.value, 0);

    const result = chartData.reduce((acc, item) => {
        acc[item.name.toLowerCase()] = item.value / total;
        return acc;
    }, {});

    const barHeight = "58px";
    return (
        <div>
            <div className="sentiment-bar-container" onMouseDown={(e) => e.stopPropagation()}>
                <div className="sentiment-container">
                    <div className="sentiment positive" style={{  fontSize:`${labelFontSize}px` }}>
                        <div className="color-box"  style={{ backgroundColor: COLORS.Positive , fontSize:`${labelFontSize}px` }}></div>
                        Positive
                    </div>
                    <div className="sentiment neutral" style={{  fontSize:`${labelFontSize}px` }}>
                        <div className="color-box" style={{ backgroundColor: COLORS.Neutral, fontSize:`${labelFontSize}px` }}></div>
                        Neutral
                    </div>
                    <div className="sentiment negative" style={{  fontSize:`${labelFontSize}px` }}>
                        <div className="color-box" style={{ backgroundColor: COLORS.Negative, fontSize:`${labelFontSize}px` }}></div>
                        Negative
                    </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column-reverse", padding: "2%", marginTop: "2%" }}>
                    <div
                        style={{
                            height: barHeight,
                            width: "100%",
                            display: "flex",
                            backgroundColor: "#fff",
                            columnGap: "1px",
                            flexDirection: "row-reverse",
                        }}
                    >

                        {chartData.map((concept, index) => (
                            <TooltipAnT
                                key={index}
                                title={
                                    <div>
                                        <strong>{concept.name}</strong>: {(concept.value * 100).toFixed(2)}%
                                    </div>
                                }
                            >
                                <div
                                    style={{
                                        height: barHeight,
                                        width: `${(concept.value / totalValue) * 100}%`,
                                        background: COLORS[concept.name],
                                        borderRadius: "0 2px 2px 0",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => handleBarClick(concept)}
                                />
                            </TooltipAnT>
                        ))}
                        <div style={{
                            display: "flex",
                            columnGap: "5px",
                            minWidth: "90px",
                            paddingRight: labelFontSize+20,
                            alignItems: "center",
                            fontSize: labelFontSize,
                        }}>
                <span className={'negative'}>
                     {(result.negative * 100).toFixed(0)}%
                </span>
                            <span className={'neutral'}>
                     {(result.neutral * 100).toFixed(0)}%
                </span>

                            <span className={'positive'}>
                    {(result.positive * 100).toFixed(0)}%
                </span>
                        </div>
                    </div>
                </div>
                `
                <OverallSentimentDetailsModal
                    props={props}
                    include_sentiment_on_concepts={true}
                    selectedSentiment={selectedSentiment}
                    setSelectedSentiment={setSelectedSentiment}
                    topConceptsBySentiment={topConceptsBySentiment}
                />
            </div>
        </div>
    );
};
